import { useEffect, useState, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Sheet,
  SheetContent,
  SheetClose,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, X, Shield } from 'lucide-react';
import { ClinicUser } from '@/services/clinicUserService';
import { roleService, Role } from '@/services/roleService';
import { toast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import { userFormSchema, UserFormData } from '@/validators/user.schema';

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: ClinicUser | null;
  onSave: (userData: Partial<ClinicUser> & { role_id: string }) => void;
  isLoading?: boolean;
}

const UserFormDialog: React.FC<UserFormDialogProps> = ({
  open,
  onOpenChange,
  user,
  onSave,
  isLoading = false,
}) => {
  const { clinic } = useClinic();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const form = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      role_id: '',
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = form;
  const role_id = watch('role_id');

  // Fetch roles on mount
  useEffect(() => {
    const fetchRoles = async () => {
      if (!clinic?.id) return;

      try {
        setLoadingRoles(true);
        const response = await roleService.getClinicRoles(clinic.id, {
          page: 1,
          limit: 100,
          status: 'active',
        });
        setRoles(response.data.roles);
      } catch (error: any) {
        toast({
          title: 'Error',
          description: 'Failed to fetch roles',
          variant: 'destructive',
        });
      } finally {
        setLoadingRoles(false);
      }
    };

    if (open) {
      fetchRoles();
    }
  }, [clinic?.id, open]);

  useEffect(() => {
    if (user) {
      reset({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        role_id: user.role?.id || '',
      });
    } else {
      reset({
        first_name: '',
        last_name: '',
        email: '',
        role_id: '',
      });
    }
  }, [user, open, reset]);

  const onSubmit = (data: UserFormData) => {
    onSave(data as Partial<ClinicUser> & { role_id: string });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full !max-w-[540px] rounded-l-2xl p-0 flex flex-col gap-0 [&>button]:hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <SheetTitle className="font-semibold text-slate-900" style={{ fontSize: '1.05rem' }}>
            {user ? 'Edit User' : 'Add User'}
          </SheetTitle>
          <SheetClose asChild>
            <button className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors">
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </SheetClose>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <FormProvider {...form}>
            <form id="user-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="uppercase text-[0.8rem] font-semibold text-[#60378B]">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="first_name">
                      First Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="first_name"
                      {...register('first_name')}
                      onChange={e =>
                        setValue('first_name', e.target.value.trimStart(), { shouldValidate: true })
                      }
                      placeholder="Enter first name"
                      className={errors.first_name ? 'border-red-500' : ''}
                    />
                    {errors.first_name && (
                      <p className="text-[0.8rem] text-red-500">{errors.first_name.message}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      {...register('last_name')}
                      onChange={e =>
                        setValue('last_name', e.target.value.trimStart(), { shouldValidate: true })
                      }
                      placeholder="Enter last name"
                    />
                    {errors.last_name && (
                      <p className="text-[0.8rem] text-red-500">{errors.last_name.message}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="text"
                    {...register('email')}
                    onChange={e => setValue('email', e.target.value.trim(), { shouldValidate: true })}
                    placeholder="Enter email address"
                    disabled={!!user}
                    className={`${user ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''} ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {user && (
                    <p className="text-xs text-slate-500">Email cannot be changed after creation</p>
                  )}
                  {errors.email && (
                    <p className="text-[0.8rem] text-red-500">{errors.email.message}</p>
                  )}
                </div>
              </div>

              {/* Role Assignment */}
              <div className="space-y-4">
                <h3 className="uppercase text-[0.8rem] font-semibold text-[#60378B]">
                  Role Assignment
                </h3>
                <div className="space-y-1.5">
                  <Label htmlFor="role_id">
                    Role <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={role_id}
                    onValueChange={value => setValue('role_id', value, { shouldValidate: true })}
                    disabled={loadingRoles}
                  >
                    <SelectTrigger className={errors.role_id ? 'border-red-500' : ''}>
                      <SelectValue placeholder={loadingRoles ? 'Loading roles...' : 'Select a role'} />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.length === 0 ? (
                        <div className="py-4 text-center">
                          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-2">
                            <Shield className="h-5 w-5 text-slate-400" />
                          </div>
                          <p className="text-sm text-slate-500">No roles available</p>
                        </div>
                      ) : (
                        roles.map(role => (
                          <SelectItem key={role.id} value={role.id}>
                            <div className="flex items-center gap-2">
                              <span>{role.name}</span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {errors.role_id && (
                    <p className="text-[0.8rem] text-red-500">{errors.role_id.message}</p>
                  )}
                  <p className="text-xs text-slate-500">
                    Define user permissions and access level
                  </p>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <Button
            variant="ghost"
            type="button"
            className="text-slate-700 hover:text-slate-900 hover:bg-transparent font-medium"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="user-form"
            className="bg-brand-teal hover:bg-brand-teal/90 rounded-lg h-11 px-8"
            disabled={isLoading || loadingRoles}
          >
            {isLoading ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{user ? 'Updating...' : 'Creating...'}</span>
              </div>
            ) : (
              <span>{user ? 'Update User' : 'Add User'}</span>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default UserFormDialog;
